import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import defaultStrings from './defaultStrings';
import { tenantId } from './tenantId';

const CONFIG_BASE_URL = (process.env.REACT_APP_TENANT_CONFIG_URL || '').replace(/\/+$/, '');
const LOAD_TIMEOUT_MS = 3000;

const TenantContext = createContext(null);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function deepMerge(base, override) {
  if (!isPlainObject(override)) return base;
  const merged = { ...base };
  for (const [key, value] of Object.entries(override)) {
    merged[key] = isPlainObject(value) && isPlainObject(base[key]) ? deepMerge(base[key], value) : value;
  }
  return merged;
}

async function loadTenantStrings(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, { cache: 'no-cache', signal: controller.signal });
    if (!response.ok) return null;
    const data = await response.json();
    return isPlainObject(data) ? data : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function loadTenantStylesheet(url) {
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.id = 'tenant-styles';
    const timer = setTimeout(resolve, LOAD_TIMEOUT_MS);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    link.addEventListener('load', done);
    link.addEventListener('error', done);
    document.head.appendChild(link);
  });
}

const XML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' };

function letterMarkIcon(name) {
  const styles = getComputedStyle(document.documentElement);
  const background = styles.getPropertyValue('--color-accent').trim() || '#4f52e3';
  const foreground = styles.getPropertyValue('--color-on-accent').trim() || '#ffffff';
  const initial = (name.trim().charAt(0) || '?').toUpperCase().replace(/[&<>"']/g, (char) => XML_ESCAPES[char]);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="9" fill="${background}"/><text x="16" y="22" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="18" font-weight="700" fill="${foreground}">${initial}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function readPath(source, path) {
  return path.split('.').reduce((node, key) => (node === undefined || node === null ? undefined : node[key]), source);
}

const pluralRules = new Intl.PluralRules();

function interpolate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (match, key) => (vars[key] === undefined || vars[key] === null ? match : String(vars[key])));
}

export function TenantProvider({ children }) {
  const [strings, setStrings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!CONFIG_BASE_URL || !tenantId) {
        setStrings(defaultStrings);
        return;
      }
      const base = `${CONFIG_BASE_URL}/${encodeURIComponent(tenantId)}`;
      const [overrides] = await Promise.all([
        loadTenantStrings(`${base}/strings.json`),
        loadTenantStylesheet(`${base}/styles.css`),
      ]);
      if (!cancelled) setStrings(deepMerge(defaultStrings, overrides));
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const t = useCallback(
    (path, vars = {}) => {
      const value = readPath(strings ?? defaultStrings, path);
      const brand = (strings ?? defaultStrings).brand;
      const context = { brand: brand.name, organization: brand.organization, ...vars };
      if (typeof value === 'string') return interpolate(value, context);
      if (isPlainObject(value) && typeof vars.count === 'number') {
        const form = value[pluralRules.select(vars.count)] ?? value.other;
        if (typeof form === 'string') return interpolate(form, context);
      }
      return vars.fallback ?? path;
    },
    [strings]
  );

  useEffect(() => {
    if (!strings) return;
    const link = document.getElementById('favicon');
    if (!link) return;
    const { faviconUrl, name } = strings.brand;
    if (faviconUrl) {
      link.removeAttribute('type');
      link.href = faviconUrl;
    } else if (name !== defaultStrings.brand.name) {
      link.type = 'image/svg+xml';
      link.href = letterMarkIcon(name);
    }
  }, [strings]);

  const value = useMemo(
    () =>
      strings
        ? { tenantId, strings, t, brand: strings.brand, features: strings.features ?? {}, list: (path) => readPath(strings, path) }
        : null,
    [strings, t]
  );

  if (!value) return null;
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used inside TenantProvider');
  return context;
}

export function useStrings() {
  return useTenant().t;
}

export function useDocumentTitle(title) {
  const { brand } = useTenant();
  useEffect(() => {
    document.title = title ? `${title} · ${brand.name}` : brand.name;
  }, [title, brand.name]);
}
